import React, { useMemo, useState } from 'react';
import { Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { swapImg } from '../../shared/utils';
import useAddToBasket from './use-add-to-basket';

import 'swiper/css';
import 'swiper/css/navigation';

import './swiper-styles.scss';

const ProductCard = ({
  product,
  userSession,
  setBasketCount,
  imageCarousel = false,
  isNested = false
}) => {
  const [selectedSize, setSelectedSize] = useState('');
  const { status, isSuccessVisible, addToBasket } = useAddToBasket({
    onAdded: () => {
      if (setBasketCount !== null) {
        setBasketCount((basketCount) => basketCount + 1);
      }
      setSelectedSize('');
    }
  });

  const [modelText, modelSize] =
    product?.product?.attributes?.modelwears?.[0]?.split(/(?<=size)/) || [];
  const selectId = useMemo(
    () =>
      `size-select-${
        product?.product?.productid || product?.productid || 'unknown'
      }`,
    [product]
  );

  return (
    <div className={`so-prod-card`}>
      {imageCarousel ? (
        <Swiper
          className="w-[45%] lg:max-w-[200px]"
          loop={true}
          mousewheel={{
            forceToAxis: true,
            sensitivity: 1,
            releaseOnEdges: false
          }}
          modules={[Pagination]}
          pagination={{ clickable: true }}
          nested={isNested}
          touchStartPreventDefault={isNested ? false : true}
        >
          {product.product.attributes.alternatives.map((slide, index) => (
            <SwiperSlide
              key={
                slide || `${product?.product?.productid || 'product'}-${index}`
              }
              className="w-full h-full cursor-grab"
            >
              <img
                alt={`${product.product.attributes.brand[0]} ${product.product.name} in ${product.product.attributes.primarycolour[0]}`}
                src={`${swapImg(slide, 'medium', 'large')}`}
                className="w-full h-full object-cover"
              />
            </SwiperSlide>
          ))}
        </Swiper>
      ) : (
        <div className="relative w-[45%] lg:h-[240px]">
          <img
            alt={`${product.product.attributes.brand[0]} ${product.product.name} in ${product.product.attributes.primarycolour[0]}`}
            src={product.product.image}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
      )}
      <div className="px-6 pb-6 flex flex-col gap-6 flex-1 h-full">
        <div className="flex flex-col gap-6 pt-6 relative overflow-hidden lg:gap-6">
          {status === 'validation' && (
            <div
              className={`absolute top-0 right-0 z-10 w-full h-full fade-in-down bg-[#24E57B] flex items-center justify-center transition-transform duration-500 ease-in-out ${
                isSuccessVisible ? 'translate-y-0' : '-translate-y-full'
              }`}
            >
              <p className="text-black font-bold text-center !text-md tracking-normal">
                Added to basket
              </p>
            </div>
          )}
          <a
            href={product.product.url + userSession}
            className="group mb-8 cursor-pointer"
          >
            <strong className="!text-md tracking-normal">
              {product.product.attributes.brand[0]}
            </strong>
            <span className="block mt-2 !text-base tracking-normal group-hover:underline">
              {product.product.name}
            </span>
          </a>
          <div>
            <p className="product-price flex flex-row items-center gap-2 text-black !text-base tracking-normal">
              {product.product.option.price ===
              product.product.option.promotionprice ? (
                <b>{product.product.option.price}</b>
              ) : (
                <>
                  <span className="rrp">{product.product.option.price}</span>
                  <b className="sale">
                    {product.product.option.promotionprice}
                  </b>
                </>
              )}
              <span className="block w-8 rotate-90 h-px bg-[#5e5e5e]/25" />
              <span className="!text-sm text-[#5e5e5e] tracking-normal">
                Colour:{' '}
                <b className="text-black">
                  {product.product.attributes.primarycolour[0]}
                </b>
              </span>
            </p>
          </div>
          <span className="block w-full h-px bg-[#5e5e5e]/25" />
        </div>
        {product.product.attributes.modelwears && (
          <p className="!text-xs tracking-normal text-[#797979]">
            {modelText}
            <span className="text-black">{modelSize}</span>
          </p>
        )}
        <div className="flex flex-row justify-between gap-6">
          <div
            className={`w-full border-2 border-solid relative hover:border-black transition-colors duration-300
              ${!selectedSize ? 'border-transparent' : 'border-black'}`}
          >
            <select
              id={selectId}
              className="w-full border-none h-full px-4 py-6 font-bold !text-base tracking-normal cursor-pointer"
              onChange={(e) => setSelectedSize(e.target.value)}
              value={selectedSize}
            >
              <option value="">Select Size</option>
              {product.product.options.map((option) => (
                <option key={option.sku} value={option.sku}>
                  {option.attributes.non_mapped_size[0]}
                </option>
              ))}
            </select>
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="absolute top-1/2 right-4 -translate-y-1/2 pointer-events-none"
            >
              <path
                d="M14.0306 8.53063L9.53063 13.0306C9.38973 13.1715 9.19863 13.2507 8.99938 13.2507C8.80012 13.2507 8.60902 13.1715 8.46813 13.0306C8.32723 12.8897 8.24807 12.6986 8.24807 12.4994C8.24807 12.3001 8.32723 12.109 8.46813 11.9681L11.6875 8.75H2.5C2.30109 8.75 2.11032 8.67098 1.96967 8.53033C1.82902 8.38968 1.75 8.19892 1.75 8C1.75 7.80109 1.82902 7.61032 1.96967 7.46967C2.11032 7.32902 2.30109 7.25 2.5 7.25H11.6875L8.46937 4.03C8.32848 3.88911 8.24932 3.69801 8.24932 3.49875C8.24932 3.2995 8.32848 3.1084 8.46937 2.9675C8.61027 2.82661 8.80137 2.74745 9.00062 2.74745C9.19988 2.74745 9.39098 2.82661 9.53187 2.9675L14.0319 7.4675C14.1018 7.53727 14.1573 7.62017 14.1951 7.71144C14.2329 7.80271 14.2523 7.90054 14.2522 7.99932C14.252 8.09811 14.2324 8.1959 14.1944 8.28708C14.1564 8.37825 14.1007 8.46102 14.0306 8.53063Z"
                fill="black"
              />
            </svg>
          </div>
          <button
            disabled={!selectedSize}
            className={`p-4 relative disabled:bg-[#979797] transition-colors duration-300 disabled:cursor-not-allowed aspect-[1/1] flex items-center justify-center ${
              status === 'validation'
                ? 'bg-[#24E57B]'
                : status === 'error'
                ? 'bg-[#FF0000]'
                : 'bg-black'
            }`}
            onClick={() => {
              if (selectedSize) {
                addToBasket(selectedSize);
              }
            }}
            aria-label="Add selected size to basket"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {status === 'validation' && (
                <path
                  d="M21.7959 7.54597L9.79594 19.546C9.69143 19.6509 9.56723 19.7341 9.43049 19.7909C9.29374 19.8476 9.14713 19.8769 8.99907 19.8769C8.851 19.8769 8.70439 19.8476 8.56765 19.7909C8.4309 19.7341 8.30671 19.6509 8.20219 19.546L2.95219 14.296C2.84755 14.1913 2.76453 14.0671 2.7079 13.9304C2.65127 13.7936 2.62212 13.6471 2.62212 13.4991C2.62212 13.3511 2.65127 13.2046 2.7079 13.0678C2.76453 12.9311 2.84755 12.8069 2.95219 12.7022C3.05684 12.5976 3.18107 12.5146 3.3178 12.4579C3.45453 12.4013 3.60107 12.3721 3.74907 12.3721C3.89706 12.3721 4.04361 12.4013 4.18033 12.4579C4.31706 12.5146 4.4413 12.5976 4.54594 12.7022L9 17.1563L20.2041 5.9541C20.4154 5.74276 20.7021 5.62402 21.0009 5.62402C21.2998 5.62402 21.5865 5.74276 21.7978 5.9541C22.0092 6.16544 22.1279 6.45209 22.1279 6.75098C22.1279 7.04986 22.0092 7.33651 21.7978 7.54785L21.7959 7.54597Z"
                  fill="black"
                />
              )}
              {status === 'default' && (
                <path
                  d="M20.25 5.62492H16.8591C16.7601 4.40418 16.2053 3.26541 15.305 2.4351C14.4046 1.6048 13.2247 1.1438 12 1.1438C10.7753 1.1438 9.59538 1.6048 8.69504 2.4351C7.79471 3.26541 7.23989 4.40418 7.14094 5.62492H3.75C3.25272 5.62492 2.77581 5.82247 2.42417 6.1741C2.07254 6.52573 1.875 7.00264 1.875 7.49992V18.7499C1.875 19.2472 2.07254 19.7241 2.42417 20.0757C2.77581 20.4274 3.25272 20.6249 3.75 20.6249H20.25C20.7473 20.6249 21.2242 20.4274 21.5758 20.0757C21.9275 19.7241 22.125 19.2472 22.125 18.7499V7.49992C22.125 7.00264 21.9275 6.52573 21.5758 6.1741C21.2242 5.82247 20.7473 5.62492 20.25 5.62492ZM12 3.37492C12.6312 3.37499 13.2412 3.60247 13.7183 4.0157C14.1954 4.42892 14.5076 5.00022 14.5978 5.62492H9.40219C9.49236 5.00022 9.80459 4.42892 10.2817 4.0157C10.7588 3.60247 11.3688 3.37499 12 3.37492ZM19.875 18.3749H4.125V7.87492H7.125V8.99992C7.125 9.29829 7.24353 9.58444 7.4545 9.79542C7.66548 10.0064 7.95163 10.1249 8.25 10.1249C8.54837 10.1249 8.83452 10.0064 9.0455 9.79542C9.25647 9.58444 9.375 9.29829 9.375 8.99992V7.87492H14.625V8.99992C14.625 9.29829 14.7435 9.58444 14.9545 9.79542C15.1655 10.0064 15.4516 10.1249 15.75 10.1249C16.0484 10.1249 16.3345 10.0064 16.5455 9.79542C16.7565 9.58444 16.875 9.29829 16.875 8.99992V7.87492H19.875V18.3749Z"
                  fill="white"
                />
              )}
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export const ProductCardSkeleton = () => {
  return (
    <div className="w-full bg-white flex flex-row">
      <div className="relative w-[45%] lg:h-[240px] bg-gray-200 animate-pulse" />
      <div className="px-6 pb-6 flex flex-col gap-6 flex-1 h-full">
        <div className="flex flex-col gap-6 pt-6 relative overflow-hidden lg:gap-6">
          <div className="group mb-8 cursor-pointer">
            <strong className="!text-md tracking-normal text-gray-200 bg-gray-200 animate-pulse">
              Brand Name
            </strong>
            <span className="block mt-2 !text-base tracking-normal group-hover:underline text-gray-200 bg-gray-200 animate-pulse">
              Product Name
            </span>
          </div>
          <div>
            <p className="product-price flex flex-row items-center gap-2 !text-base tracking-normal">
              <b className="text-gray-200 bg-gray-200 animate-pulse">£100.00</b>
              <span className="block w-8 rotate-90 h-px bg-[#5e5e5e]/25" />
              <span className="!text-sm text-[#5e5e5e] tracking-normal">
                Colour:{' '}
                <b className="text-gray-200 bg-gray-200 animate-pulse">Black</b>
              </span>
            </p>
          </div>
          <span className="block w-full h-px bg-[#5e5e5e]/25" />
        </div>
        <p className="!text-xs tracking-normal text-gray-200 bg-gray-200 animate-pulse w-[calc(100%-48px)]">
          Model Wears
        </p>
        <div className="flex flex-row justify-between gap-6">
          <div className="w-full border-2 border-solid relative text-gray-200 bg-gray-200 animate-pulse">
            <select
              className="w-full border-none h-full px-4 py-6 font-bold !text-base tracking-normal cursor-pointer"
              defaultValue=""
              disabled
            >
              <option value="">Loading...</option>
            </select>
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="absolute top-1/2 right-4 -translate-y-1/2 pointer-events-none text-gray-200"
            >
              <path
                d="M14.0306 8.53063L9.53063 13.0306C9.38973 13.1715 9.19863 13.2507 8.99938 13.2507C8.80012 13.2507 8.60902 13.1715 8.46813 13.0306C8.32723 12.8897 8.24807 12.6986 8.24807 12.4994C8.24807 12.3001 8.32723 12.109 8.46813 11.9681L11.6875 8.75H2.5C2.30109 8.75 2.11032 8.67098 1.96967 8.53033C1.82902 8.38968 1.75 8.19892 1.75 8C1.75 7.80109 1.82902 7.61032 1.96967 7.46967C2.11032 7.32902 2.30109 7.25 2.5 7.25H11.6875L8.46937 4.03C8.32848 3.88911 8.24932 3.69801 8.24932 3.49875C8.24932 3.2995 8.32848 3.1084 8.46937 2.9675C8.61027 2.82661 8.80137 2.74745 9.00062 2.74745C9.19988 2.74745 9.39098 2.82661 9.53187 2.9675L14.0319 7.4675C14.1018 7.53727 14.1573 7.62017 14.1951 7.71144C14.2329 7.80271 14.2523 7.90054 14.2522 7.99932C14.252 8.09811 14.2324 8.1959 14.1944 8.28708C14.1564 8.37825 14.1007 8.46102 14.0306 8.53063Z"
                fill="currentColor"
              />
            </svg>
          </div>
          <button
            disabled
            className={`p-4 relative disabled:bg-[#979797] transition-colors duration-300 disabled:cursor-not-allowed aspect-[1/1] flex items-center justify-center`}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M20.25 5.62492H16.8591C16.7601 4.40418 16.2053 3.26541 15.305 2.4351C14.4046 1.6048 13.2247 1.1438 12 1.1438C10.7753 1.1438 9.59538 1.6048 8.69504 2.4351C7.79471 3.26541 7.23989 4.40418 7.14094 5.62492H3.75C3.25272 5.62492 2.77581 5.82247 2.42417 6.1741C2.07254 6.52573 1.875 7.00264 1.875 7.49992V18.7499C1.875 19.2472 2.07254 19.7241 2.42417 20.0757C2.77581 20.4274 3.25272 20.6249 3.75 20.6249H20.25C20.7473 20.6249 21.2242 20.4274 21.5758 20.0757C21.9275 19.7241 22.125 19.2472 22.125 18.7499V7.49992C22.125 7.00264 21.9275 6.52573 21.5758 6.1741C21.2242 5.82247 20.7473 5.62492 20.25 5.62492ZM12 3.37492C12.6312 3.37499 13.2412 3.60247 13.7183 4.0157C14.1954 4.42892 14.5076 5.00022 14.5978 5.62492H9.40219C9.49236 5.00022 9.80459 4.42892 10.2817 4.0157C10.7588 3.60247 11.3688 3.37499 12 3.37492ZM19.875 18.3749H4.125V7.87492H7.125V8.99992C7.125 9.29829 7.24353 9.58444 7.4545 9.79542C7.66548 10.0064 7.95163 10.1249 8.25 10.1249C8.54837 10.1249 8.83452 10.0064 9.0455 9.79542C9.25647 9.58444 9.375 9.29829 9.375 8.99992V7.87492H14.625V8.99992C14.625 9.29829 14.7435 9.58444 14.9545 9.79542C15.1655 10.0064 15.4516 10.1249 15.75 10.1249C16.0484 10.1249 16.3345 10.0064 16.5455 9.79542C16.7565 9.58444 16.875 9.29829 16.875 8.99992V7.87492H19.875V18.3749Z"
                fill="white"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
